export const TEMPLATE_NAMES = ['USER_GROUP_INVITE', 'USER_GROUP_INVITE_REMINDER'] as const;
export type TemplateName = typeof TEMPLATE_NAMES[number];

export interface NotificationTemplate {
  _id: string;
  _name: string;
  _title: string;
  _message: string;
  _format: string;
  _locale?: string;
  _appId?: string;
}

/**
 * Training application invite email template.
 *
 * Standard Passport variables (substituted by any caller — Console App,
 * onboarding workflow, CCRA, etc.):
 *   ${base_url}      — API origin, used for the logo img src
 *   ${body_header}   — large dark heading (e.g. app name)
 *   ${name}          — pink subtitle (e.g. project / user group name)
 *   ${body_content}  — opening paragraph / greeting text
 *   ${inviter_name}  — person or system that triggered the invite
 *   ${subject}       — email subject line (also used in <title>)
 *   ${invite_link}   — unique per-user accept URL (required by Passport)
 *
 * The Twinit Academy resources section, support contacts, and next-steps
 * summary below are HARDCODED — they appear in every email sent to this
 * training application, regardless of which system triggered the invite.
 * This means even a short Console-App invite ("Administrator has invited you
 * to join the App.") will still deliver the full onboarding context.
 */
export const SUGGESTED_INVITE_TEMPLATE = `<!doctype html>
<html xmlns="http://www.w3.org/1999/xhtml">
  <head>
    <meta charset="UTF-8">
    <meta http-equiv="X-UA-Compatible" content="IE=edge">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>\${subject}</title>
    <style>
      @media only screen and (min-width:768px) {
        .templateContainer { width: 600px !important; }
      }
      @media only screen and (max-width:480px) {
        .templateContainer { width: 300px !important; }
        .h1 { font-size: 22px !important; line-height: 125% !important; }
        .h2 { font-size: 20px !important; line-height: 125% !important; }
        .p  { font-size: 16px !important; line-height: 150% !important; }
        .logo img { width: 100% !important; }
        .resource-block { padding: 12px !important; }
      }
      .resource-title { color: #c71784; font-size: 16px; font-weight: bold; margin: 18px 0 4px 0; }
      .resource-link  { color: #1a73e8; }
      .resource-desc  { font-size: 14px; color: #555555; line-height: 160%; margin: 4px 0 0 0; }
    </style>
  </head>
  <body style="background-color: #fafafa;">
    <div class="templateContainer" style="margin: 40px auto; width: 600px; border-radius: 25px; background-color: #FFFFFF; padding-top: 24px; padding-bottom: 64px;">

      <!-- Logo — \${base_url} resolves correctly on every environment -->
      <div style="padding: 9px; text-align: center;" class="logo">
        <img align="center" alt="Invicara" src="\${base_url}/passportsvc/api/et/logo.png"
             width="320" style="max-width:1200px; display:inline !important; vertical-align:bottom; border:0; height:auto; outline:none; text-decoration:none;">
      </div>

      <div class="content" style="padding: 0 18px 9px; color: #333333; font-family: Helvetica; font-size: 16px; line-height: 160%; text-align: left;">

        <!-- Dynamic header — provided by the caller -->
        <div style="padding-top: 9px;">
          <h1 class="h1" style="margin:0; padding:0; color:#333333; font-family:Helvetica; font-size:28px; font-weight:bold; line-height:150%; text-align:center;">
            \${body_header}
          </h1>
          <h2 class="h2" style="margin:0; padding:0; color:#c71784; font-family:Helvetica; font-size:22px; font-weight:bold; line-height:125%; text-align:center;">
            \${name}
          </h2>
        </div>

        <!-- Dynamic greeting — provided by the caller.
             Console App sends: "Administrator has invited you to join the App."
             Onboarding workflow sends a personalised paragraph. -->
        <div class="p" style="margin: 10px 0; padding: 24px 48px;">
          \${body_content}
        </div>

        <!-- ═══════════════════════════════════════════════════════════════════
             HARDCODED training content — always present regardless of caller.
             ═══════════════════════════════════════════════════════════════ -->

        <!-- Resources -->
        <div class="resource-block" style="margin: 0 48px; padding: 20px 24px; background-color: #f8f9fa; border-radius: 10px; font-size: 14px; line-height: 160%;">

          <div class="resource-title">digitaltwin-factory Community</div>
          <a class="resource-link" href="https://community.digitaltwin-factory.com/">https://community.digitaltwin-factory.com/</a>
          <p class="resource-desc">This is our open-source developer community. Here you can find forums, knowledgebases, links to open-source examples and code, guides, and much more. The &ldquo;Ask the Community&rdquo; forum is a great way to ask questions and get answers straight from Twinit developers.</p>

          <div class="resource-title">Twinit Academy</div>
          <a class="resource-link" href="https://twinit.dev/docs/academy/index/">https://twinit.dev/docs/academy/index/</a>
          <p class="resource-desc">Twinit Academy contains all of the training material and is available on twinit.dev. We recommend you proceed through the training in the suggested order, as the training material builds on the courses and lessons that came before.</p>

          <div class="resource-title">Twinit Academy Training Application</div>
          <p class="resource-desc">You will receive an invite from Twinit to a Twinit Academy training application on the Twinit Sandbox environment. If you don&rsquo;t see it, check your Spam or Junk folder. If the email does not come through you can use this link to accept the invite as well:
          <br/><a class="resource-link" href="https://sandbox-api.invicara.com/passportsvc/api/accounts#invitation">https://sandbox-api.invicara.com/passportsvc/api/accounts#invitation</a>
          <br/><br/>
          The Twinit Academy application is private to your organisation&rsquo;s users and should be used to complete all the training hands-on exercises. The email invitation will contain a link that you can follow to accept the invite in the Twinit console application. After accepting the invite you can feel free to close the console. The Twinit Academy courses will take over from there.
          <br/><br/>
          It&rsquo;s important to note that you will need to create a second Twinit user when accepting this invite. The training application is created on our Sandbox environment. When you filled out the digitaltwin-factory request form, you created a Twinit Production user. That user will not work on the Sandbox instance of Twinit.</p>

          <div class="resource-title">twinit.dev</div>
          <a class="resource-link" href="https://twinit.dev/">https://twinit.dev/</a>
          <p class="resource-desc">You can find all our Developer Documentation on twinit.dev. You already have access to twinit.dev as no authentication is needed to access it.</p>

          <div class="resource-title">@dtplatform node repository</div>
          <p class="resource-desc">You will also automatically have access to the @dtplatform node repository enabling you to install the Twinit JavaScript SDK. The React UI training courses will walk through how to install the modules.</p>

        </div>

        <!-- Support -->
        <div style="margin: 20px 48px 0; padding: 16px 20px; border-left: 3px solid #c71784; font-size: 14px; line-height: 170%; color: #555;">
          <strong>Support</strong><br/>
          Multiple avenues of support are available for you based on how you are most comfortable interacting with us.<br/><br/>
          We like to encourage use of the &ldquo;Ask the Community&rdquo; forum as questions and answers there help out other developers as well.<br/><br/>
          You can log a support ticket at <a href="https://support.twinit.com/support/home">https://support.twinit.com/support/home</a>.<br/><br/>
          Or you can feel free to reach out to Scott (<a href="mailto:scott.mollon@invicara.com">scott.mollon@invicara.com</a>),
          Dominika (<a href="mailto:dominika.oles@invicara.com">dominika.oles@invicara.com</a>),
          or Ibrahim (<a href="mailto:ibrahim.firat@invicara.com">ibrahim.firat@invicara.com</a>) directly over email for support as well.
        </div>

        <!-- Next steps -->
        <div style="margin: 20px 48px 0; padding: 16px 20px; background-color: #fff3e0; border-radius: 8px; font-size: 14px; line-height: 170%; color: #333;">
          <strong>&#9989; In Summary, be sure to:</strong><br/>
          <strong>(1)</strong> Accept the email invitation to the Twinit Academy Training Application on Sandbox<br/>
          <strong>(2)</strong> Explore the digitaltwin-factory community<br/>
          <strong>(3)</strong> Begin your Twinit journey at Twinit Academy
        </div>

      </div>

      <!-- CTA Button -->
      <div style="margin: 24px auto 0; text-align: center;">
        <span style="text-align:center; border-collapse:separate !important; border-radius:11px; background-color:#C71784; font-family:Arial; font-size:18px; padding:16px 32px; display:inline-block;">
          <a title="Accept Invitation" href="\${invite_link}" target="_blank"
             style="font-weight:bold; letter-spacing:normal; line-height:100%; text-align:center; text-decoration:none; color:#FFFFFF;">
            Accept Invitation
          </a>
        </span>
      </div>

    </div>
  </body>
</html>`;

/**
 * Reminder email. Uses ${body_header}, ${name}, ${invite_link}, ${base_url}.
 */
export const SUGGESTED_REMINDER_TEMPLATE = `<!doctype html>
<html xmlns="http://www.w3.org/1999/xhtml">
  <head>
    <meta charset="UTF-8">
    <meta http-equiv="X-UA-Compatible" content="IE=edge">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>Reminder: Invitation to \${body_header}</title>
    <style>
      @media only screen and (min-width:768px) {
        .templateContainer { width: 600px !important; }
      }
      @media only screen and (max-width:480px) {
        .templateContainer { width: 300px !important; }
        .h1 { font-size: 22px !important; line-height: 125% !important; }
        .p  { font-size: 16px !important; line-height: 150% !important; }
        .logo img { width: 100% !important; }
      }
    </style>
  </head>
  <body style="background-color: #fafafa;">
    <div class="templateContainer" style="margin: 40px auto; width: 600px; border-radius: 25px; background-color: #FFFFFF; padding-top: 24px; padding-bottom: 64px;">

      <div style="padding: 9px; text-align: center;" class="logo">
        <img align="center" alt="Invicara" src="\${base_url}/passportsvc/api/et/logo.png"
             width="320" style="max-width:1200px; display:inline !important; vertical-align:bottom; border:0; height:auto; outline:none; text-decoration:none;">
      </div>

      <div class="content" style="padding: 0 18px 9px; color: #333333; font-family: Helvetica; font-size: 18px; line-height: 150%; text-align: left;">
        <div style="padding-top: 9px;">
          <h1 class="h1" style="margin:0; padding:0; color:#333333; font-family:Helvetica; font-size:30px; font-weight:bold; line-height:150%; text-align:center;">
            Reminder: \${body_header}
          </h1>
          <h2 style="margin:0; padding:0; color:#c71784; font-family:Helvetica; font-size:22px; font-weight:bold; line-height:125%; text-align:center;">
            \${name}
          </h2>
        </div>

        <p class="p" style="margin: 10px 0; padding: 24px 48px;">
          This is a friendly reminder that you have a pending invitation to the Twinit Academy Training Application.
          This invitation will expire in 24 hours &mdash; please accept it before it does.
          <br/><br/>
          Click <strong>Accept Invitation</strong> below to accept the invite in the Twinit console.
          After accepting you can close the console &mdash; Twinit Academy will take over from there.
        </p>

        <!-- ═══════════════════════════════════════════════════════════════════
             HARDCODED training content — always present regardless of caller.
             ═══════════════════════════════════════════════════════════════ -->

        <div style="margin: 0 24px; padding: 20px 24px; background-color: #f8f9fa; border-radius: 10px; font-size: 14px; line-height: 160%;">

          <div style="color: #c71784; font-size: 16px; font-weight: bold; margin: 0 0 4px 0;">digitaltwin-factory Community</div>
          <a style="color: #1a73e8;" href="https://community.digitaltwin-factory.com/">https://community.digitaltwin-factory.com/</a>
          <p style="font-size: 14px; color: #555555; line-height: 160%; margin: 4px 0 0 0;">Open-source developer community — forums, knowledgebases, open-source examples, and the &ldquo;Ask the Community&rdquo; forum.</p>

          <div style="color: #c71784; font-size: 16px; font-weight: bold; margin: 18px 0 4px 0;">Twinit Academy</div>
          <a style="color: #1a73e8;" href="https://twinit.dev/docs/academy/index/">https://twinit.dev/docs/academy/index/</a>
          <p style="font-size: 14px; color: #555555; line-height: 160%; margin: 4px 0 0 0;">All Twinit training material on twinit.dev. Proceed in the suggested order as courses build on each other.</p>

          <div style="color: #c71784; font-size: 16px; font-weight: bold; margin: 18px 0 4px 0;">twinit.dev</div>
          <a style="color: #1a73e8;" href="https://twinit.dev/">https://twinit.dev/</a>
          <p style="font-size: 14px; color: #555555; line-height: 160%; margin: 4px 0 0 0;">All Twinit Developer Documentation. No authentication needed.</p>

        </div>

        <div style="margin: 20px 24px 0; padding: 16px 20px; border-left: 3px solid #c71784; font-size: 14px; line-height: 170%; color: #555;">
          <strong>Support</strong><br/>
          Log a ticket at <a href="https://support.twinit.com/support/home">https://support.twinit.com/support/home</a>,
          use the &ldquo;Ask the Community&rdquo; forum, or reach out to
          Scott (<a href="mailto:scott.mollon@invicara.com">scott.mollon@invicara.com</a>),
          Dominika (<a href="mailto:dominika.oles@invicara.com">dominika.oles@invicara.com</a>),
          or Ibrahim (<a href="mailto:ibrahim.firat@invicara.com">ibrahim.firat@invicara.com</a>) directly.
        </div>

      </div>

      <div style="margin: 24px auto 0; text-align: center;">
        <span style="text-align:center; border-collapse:separate !important; border-radius:11px; background-color:#C71784; font-family:Arial; font-size:18px; padding:16px 32px; display:inline-block;">
          <a title="Accept Invitation" href="\${invite_link}" target="_blank"
             style="font-weight:bold; letter-spacing:normal; line-height:100%; text-align:center; text-decoration:none; color:#FFFFFF;">
            Accept Invitation
          </a>
        </span>
      </div>

    </div>
  </body>
</html>`;
